import { createSlice } from "@reduxjs/toolkit";
import {IServer} from "./ServerSlice";
import {IResolution} from "./ResolutionSlice";
import {IStreamStatus} from "./StatusSlice";

export type StreamFormat = 'RTMP'|'HLS'|'WSS'|'DASH';
export type StreamQuality = 'SD'|'HD'|'FHD'|'UHD'|'HV'|'SHV';

export interface IStreamLink {
	format: StreamFormat,
	url: string,
}

export interface IStream {
	id: number,
	key: number,
	server_id: number,
	description: string|null,
	sport: string,
	monitor_id: number,
	url: string|null,
	tournament: string,
	team1: string,
	team2: string,
	start_date: string,
	start_time: string,
	resolution_id: number,
	status_id: number,
	created_by: number,
	created_at: string|null,
	updated_at: string|null,
	server: IServer,
	resolution: IResolution,
	status: IStreamStatus,
	links: IStreamLink[],
}

export interface StreamState {
	streams: IStream[],
	stream: IStream|null,
	search: IStream[],

	creating: boolean,
	deleting: boolean,
	updating: boolean,
	restoring: boolean,
	gettingList: boolean,
	gettingItem: boolean,
	searching: boolean,

	isError: boolean,
	errorCode: number,
	errorMessage: string|null,
}

export const initialState: StreamState = {
	streams: [],
	stream: null,
	search: [],

	creating: false,
	deleting: false,
	updating: false,
	restoring: false,
	gettingList: false,
	gettingItem: false,
	searching: false,

	isError: false,
	errorCode: 0,
	errorMessage: null,
};

export const StreamSlice = createSlice({
	name: 'stream',
	initialState,
	reducers: {

		clearStreamList: state => {
			return { ...state, streams: [] };
		},
		clearStreamItem: state => {
			return { ...state, stream: null };
		},

		clearStreamSearchResult: (state, action): StreamState => {
			return { ...state, search: [] };
		},

		onLoadFindByKey: (state, action ): StreamState => {
			return {...state, searching: true};
		},
		onLoadCreate: (state, action ): StreamState => {
			return {...state, creating: true};
		},
		onLoadDelete: (state, action ): StreamState => {
			return {...state, deleting: true};
		},
		onLoadRestore: (state, action ): StreamState => {
			return {...state, restoring: true};
		},
		onLoadArchive: (state, action ): StreamState => {
			return {...state, deleting: true};
		},
		onLoadUpdate: (state, action ): StreamState => {
			return {...state, updating: true};
		},
		onLoadUpdateStatuses: (state, action ): StreamState => {
			return {...state, updating: true};
		},
		onLoadList: (state, action ): StreamState => {
			return {...state, gettingList: true};
		},
		onLoadItem: (state, action ): StreamState => {
			return {...state, gettingItem: true};
		},
		onLoadSearch: (state, action ): StreamState => {
			return {...state, search: [], searching: true};
		},

		// Success
		onSuccessCreate: (state, action): StreamState => {
			return {...state, creating: false};
		},
		onSuccessFindByKey: (state, action): StreamState => {
			return {...state, stream: action.payload.data, searching: false};
		},
		onSuccessDelete: (state, action): StreamState => {
			return {...state, deleting: false};
		},
		onSuccessRestore: (state, action): StreamState => {
			return {...state, restoring: false};
		},
		onSuccessUpdate: (state, action): StreamState => {
			return {...state, updating: false};
		},
		onSuccessUpdateStatuses: (state, action): StreamState => {
			return {...state, updating: false};
		},
		onSuccessList: (state, action): StreamState => {
			return {...state, streams: action.payload.data.data, gettingList: false};
		},
		onSuccessItem: (state, action): StreamState => {
			return {...state, stream: action.payload.data, gettingItem: false};
		},
		onSuccessSearch: (state, action): StreamState => {
			return {...state, search: action.payload.data, searching: false};
		},

		// Errors
		onFailCreate: (state, action): StreamState => {
			return {...state, creating: false };
		},
		onFailFindByKey: (state, action): StreamState => {
			return {...state, searching: false };
		},
		onFailDelete: (state, action): StreamState => {
			return {...state, stream: null, deleting: false };
		},
		onFailRestore: (state, action): StreamState => {
			return {...state, restoring: false };
		},
		onFailUpdate: (state, action): StreamState => {
			return {...state, updating: false };
		},
		onFailUpdateStatuses: (state, action): StreamState => {
			return {...state, updating: false };
		},
		onFailList: (state, action): StreamState => {
			return {...state, streams: [] };
		},
		onFailItem: (state, action): StreamState => {
			return {...state, stream: null };
		},
		onFailSearch: (state, action): StreamState => {
			return { ...state, search: [], searching: false };
		},
	},
});

export const {
	clearStreamItem,
	clearStreamList,
	clearStreamSearchResult
} = StreamSlice.actions;
